/* eslint-disable max-lines */
import { AuditAuthor } from "./IAuditAuthor";
import { IStationModel } from "./IStations";

export interface IGTFSShape {
  id: number;
  name: string;
  shapeId: string;
  startingLatitude: number;
  startingLongitude: number;
  endingLatitude: number;
  endingLongitude: number;
  stationFromId: number;
  stationToId: number;
}

export interface IGTFSShapeForm {
  name: string;
  stationFromId: Partial<IStationModel>;
  stationToId: Partial<IStationModel>;
  kmlFile: FileList | null;
}

export interface IGTFSStation {
  id: number;
  name: string;
  latitude: number;
  longitude: number;
  // True when the field was set by hand for the feed; false means the feed follows the station.
  isNameEdited?: boolean;
  isLocationEdited?: boolean;
}

export interface IGTFSStationForm {
  id: Partial<IStationModel>;
  name: string;
  latitude: number;
  longitude: number;
}

// A GTFS-only added stop as the backend lists it: the stored row plus its trip, line and station,
// with the times as clocks (the row stores minutes after the trip's departure).
export interface IGTFSTripStop {
  id: number;
  tripId: number;
  lineId: number;
  lineName: string | null;
  tripDepartureTime: string | null;
  tripLengthMinutes: number;
  stationId: number;
  stationName: string | null;
  arrivalMinutesAfterDeparture: number;
  departureMinutesAfterDeparture: number;
  arrivalClock: string;
  arrivalDayOffset: number;
  departureClock: string;
  departureDayOffset: number;
  allowsPickup: boolean;
  allowsDropOff: boolean;
  isConfirmed: boolean;
  source: string | null;
  // True when the stop is not before the trip's arrival, so the feed leaves it out.
  fallsAfterTripArrival: boolean;
}

export interface IGTFSTripStopUpsert {
  tripId: number;
  stationId: number;
  arrivalMinutesAfterDeparture: number;
  departureMinutesAfterDeparture: number | null;
  // No boarding flags: every new stop is a rest stop, and an update leaves the row's flags alone.
  isConfirmed: boolean;
  source: string | null;
}

export interface IGTFSTripStopForm {
  lineId: number | string;
  tripId: number | string;
  station: Partial<IGTFSStation> | null;
  // Time after the trip's departure, "H:mm" or "HH:mm"; hours may pass 24 for a later day.
  offset: string;
}

export interface IGTFSTrip {
  gtfsLineId: number;
  gtfsLine: null;
  id: number;
}

export interface IGTFSLine extends AuditAuthor{
  gtfsTrips: IGTFSTrip[];
  gtfsShape: IGTFSShape;
  lineName: string;
  operatorName: string;
  id: number;
}
export interface IGTFSLineSelectItem {
  id: number;
  name: string;
}

export interface IGTFSLineCreateForm {
  lineId?: {
    label: string;
    parentValue: number;
    oldValue: number;
    value: number;
    selected: boolean;
  };
  shapeId?: IGTFSLineSelectItem;
  trips: Record<string, { id: number; selected: boolean }>;
}

export interface IGTFSLineDetailsResponse {
    gtfsTrips: IGTFSTrip[];
    gtfsShape: IGTFSShape;
    lineName: string | null;
    id: number;
}

export interface IGTFSLineUpsertForm {
  lineId: number;
  shapeId: number;
  tripIds: number[];
}

export interface IGTFSOperator {
  gjAlToken: string;
  blogId: number;
  id: number;
}

export interface IGTFSOperatorResponse {
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  data: IGTFSOperator[];
}

export interface IGjAlRedirect{
  url: string;
  name: string;
}
export interface IGTFSUploadResult {
  feedUrl: string;
  gjAlRedirects: Record<string, IGjAlRedirect>;
}

export interface IGTFSValidationResult {
  id: number;
  feedVersion: number;
  isValid: boolean;
  errorCount: number;
  warningCount: number;
  errorsJson: string;
  warningsJson: string;
  totalAgencies: number;
  totalRoutes: number;
  totalTrips: number;
  totalStops: number;
  feedUrl: string;
  generatedAt: string;
}

export interface IGTFSEnrollmentLog {
  id: number;
  action: string;
  entityType: string;
  entityId: number;
  details: string;
  processedAt: string;
}

export enum GTFSBlockedEntityType {
  Operator = 1,
  Line = 2,
  Trip = 3
}

export interface IGTFSBlockedEntity extends AuditAuthor {
  id: number;
  entityType: GTFSBlockedEntityType;
  entityTypeName: string;
  entityId: number;
  reason: string | null;
}

export interface IGTFSBlockInsert {
  entityType: GTFSBlockedEntityType;
  entityId: number;
  reason?: string;
}

export interface IGTFSBlockTypeOption {
  value: GTFSBlockedEntityType;
  label: string;
}

export interface IGTFSBlockForm {
  entityType?: IGTFSBlockTypeOption;
  entityId: number;
  reason?: string;
}
